import * as c from 'models/constants';
import { combineDateAndTime, normalizeBookingTimetablesSingleResource } from 'helpers/bookingTemplates';
import { IBookingTimeTable, IBookingTimeTableForm, IBookingTimeTableGroup } from 'models/shop/bookingtimetables';
import moment = require('moment');
import { Dispatch } from 'react-redux';
import { IStore } from 'redux/IStore';
import { closeModal } from 'redux/modules/modal';
import {
	bookingTemplateResourceIds,
	createMultipleBookingTimetables,
	createMultiplePartiallyUnavailableBookingTimetables,
	deleteAllTimeTablesBetweenTimes,
	generateMultipleBookingTimeTables,
	generatePartiallyUnavailableBookingTimeTable,
	getBookingTimetables,
	setBookingTimetablesFiltersOffice,
} from 'redux/modules/shop/bookingtimetables';
import { IOffice, IOfficeTime } from 'models/shop/office';
import { lowerFirstObjectPropertiesNames } from 'helpers/common';
import { IOfficeMember } from 'models/shop/members';
import { getOfficeBookingTemplates, momentWithTimezone, sequential } from 'redux/modules/shop/helpers';
import { requestFailure } from 'redux/modules/shop/bookingtimetables';

export interface IEditWorkTimeBaseProps {
	resource: IOfficeMember;
	office: IOffice;
	monday: moment.Moment;
	timetables: IBookingTimeTableGroup[];
	partiallyUnavailableTimetables: IBookingTimeTableGroup[];
	staffIds: string[];
}

export interface IEditWeekWorkTimeOwnProps extends IEditWorkTimeBaseProps {}

export interface IEditDayWorkTimeOwnProps extends IEditWorkTimeBaseProps {
	day: number;
}

// For weekly work time edit
export function initialFormValues(
	ownProps: IEditWeekWorkTimeOwnProps | IEditDayWorkTimeOwnProps,
): IBookingTimeTableForm {
	const { monday, resource } = ownProps;
	const timetables = ownProps.timetables.filter((tt) => tt.resourceId === resource.id);
	const partiallyUnavailableTimetables = ownProps.partiallyUnavailableTimetables.filter(
		(tt) => tt.resourceId === resource.id,
	);
	const initialValues: any = [];

	const timezone = ownProps.office?.zoneInfo;

	// Go through 4 times for each week, load first week and create empty values for other weeks
	for (let i = 0; i < 4; i = i + 1) {
		if (i === 0) {
			initialValues.push(
				[0, 1, 2, 3, 4, 5, 6].map((d) => {
					const date = monday.clone().add(d, 'days');
					const groups = timetables.filter((tt) => momentWithTimezone(timezone, tt.startTime).isSame(date, 'day'));
					const partiallyUnavailableGroups = partiallyUnavailableTimetables.filter((tt) =>
						momentWithTimezone(timezone, tt.startTime).isSame(date, 'day'),
					);

					return {
						date: date.toISOString(),
						active: Boolean(groups.length),
						periods: groups.map((g) => {
							let availabilityType = 0;

							// Check for workTime and partiallyUnavailableTime difference, if same or between then set availability type to manual order only
							for (const i in partiallyUnavailableGroups) {
								const partialStartTime = partiallyUnavailableGroups[i].startTime;
								const partialEndTime = partiallyUnavailableGroups[i].endTime;

								if (
									moment(partialStartTime).isSameOrAfter(moment(g.startTime)) &&
									moment(partialEndTime).isSameOrBefore(g.endTime)
								) {
									availabilityType = 1;
								}
							}

							return {
								startTime: g.startTime,
								endTime: g.endTime,
								availabilityType,
							};
						}),
					};
				}),
			);
		} else {
			initialValues.push(
				[0, 1, 2, 3, 4, 5, 6].map((d) => {
					const date = monday.clone().add(d + i * 7, 'days');

					return {
						date: date.toISOString(),
						active: false,
						periods: [],
					};
				}),
			);
		}
	}

	return initialValues;
}

export function stateToEditWeeklyWorkTimeProps(state: IStore, ownProps: IEditWeekWorkTimeOwnProps) {
	const { monday } = ownProps;
	const lastDay = monday.clone().endOf('week');
	const roundedLastDay = lastDay.clone().startOf('hour').add(1, 'hour'); // Round to the start of the hour

	let currentFormValues: Partial<IBookingTimeTableForm> = {};

	if (state.form[c.FORM_NAME_WORK_TIME] && state.form[c.FORM_NAME_WORK_TIME].values) {
		currentFormValues = state.form[c.FORM_NAME_WORK_TIME].values;
	}

	return {
		state,
		currentFormValues,
		initialValues: {
			workTime: initialFormValues(ownProps),
			startDate: monday.toISOString(),
			endDate: roundedLastDay.toISOString(),
		},
		isFetching: state.bookingTimetables.isFetching,
	};
}

export function stateToEditDailyWorkTimeProps(state: IStore, ownProps: IEditDayWorkTimeOwnProps) {
	const { monday, resource } = ownProps;
	const timetables = ownProps.timetables.filter((tt) => tt.resourceId === resource.id);
	const partiallyUnavailableTimetables = ownProps.partiallyUnavailableTimetables.filter(
		(tt) => tt.resourceId === resource.id,
	);

	const timezone = ownProps.office?.zoneInfo;

	const workTime = [0, 1, 2, 3, 4, 5, 6].map((d) => {
		const date = monday.clone().add(d, 'days');
		const groups = timetables.filter((tt) => momentWithTimezone(timezone, tt.startTime).isSame(date, 'day'));
		const partiallyUnavailableGroups = partiallyUnavailableTimetables.filter((tt) =>
			momentWithTimezone(timezone, tt.startTime).isSame(date, 'day'),
		);

		return {
			date: date.toISOString(),
			active: Boolean(groups.length),
			periods: groups.map((g) => {
				let availabilityType = 0;

				// Check for workTime and partiallyUnavailableTime difference, if same or between then set availability type to manual order only
				for (const i in partiallyUnavailableGroups) {
					const partialStartTime = partiallyUnavailableGroups[i].startTime;
					const partialEndTime = partiallyUnavailableGroups[i].endTime;

					if (
						moment(partialStartTime).isSameOrAfter(moment(g.startTime)) &&
						moment(partialEndTime).isSameOrBefore(g.endTime)
					) {
						availabilityType = 1;
					}
				}

				return {
					startTime: g.startTime,
					endTime: g.endTime,
					availabilityType,
				};
			}),
		};
	});

	let currentFormValues: Partial<IBookingTimeTableForm> = {};

	if (state.form[c.FORM_NAME_WORK_TIME] && state.form[c.FORM_NAME_WORK_TIME].values) {
		currentFormValues = state.form[c.FORM_NAME_WORK_TIME].values;
	}

	return {
		state,
		currentFormValues,
		initialValues: { workTime: workTime },
		isFetching: state.bookingTimetables.isFetching,
	};
}

export function getOfficeTimeForDay(office: IOffice, date: moment.Moment): IOfficeTime | undefined {
	if (!office) {
		return undefined;
	}
	const d = date.isoWeekday();
	const csday = d === 7 ? 0 : d;
	return (office.officeTimes || []).map(lowerFirstObjectPropertiesNames).find((ot) => ot.day === csday);
}

export function officeMinMaxTime(office: IOffice, monday: moment.Moment, day: number): [Date | null, Date | null] {
	const date = monday.clone().add(day, 'days');
	const timezone = office?.zoneInfo;
	const time = getOfficeTimeForDay(office, date);

	if (time) {
		return [
			combineDateAndTime(date, momentWithTimezone(timezone, time.openTime), true).toDate(),
			combineDateAndTime(date, momentWithTimezone(timezone, time.closeTime), true).toDate(),
		];
	}
	return [null, null];
}

export function dayMinMaxTime(office: IOffice, monday: moment.Moment, day: number) {
	const date = monday.clone().add(day, 'days');
	const timezone = office?.zoneInfo;

	return [momentWithTimezone(timezone, date.startOf('day')), momentWithTimezone(timezone, date.endOf('day'))];
}

export function bookingTimetablesMinMaxTime(
	bookingTimetables: IBookingTimeTable[],
	resourceId: string,
	day: moment.Moment,
	timezone: string,
): [Date | null, Date | null] {
	const dailyTimeTables = bookingTimetables.filter(
		(tt) =>
			tt.resourceId === resourceId &&
			momentWithTimezone(timezone, tt.startTime).isSame(momentWithTimezone(timezone, day), 'date'),
	);
	// mybe here
	const resourceTimeLimits = [
		dailyTimeTables.reduce<moment.Moment | null>((acc, val) => {
			const current = momentWithTimezone(timezone, val.startTime);
			if (acc === null) {
				return current;
			}
			if (timezone) {
				acc = momentWithTimezone(timezone, acc);
			}
			return current.isBefore(acc) ? current : acc;
		}, null),
		dailyTimeTables.reduce<moment.Moment | null>((acc, val) => {
			const current = momentWithTimezone(timezone, val.endTime);
			if (acc === null) {
				return current;
			}
			if (timezone) {
				acc = momentWithTimezone(timezone, acc);
			}
			return current.isAfter(acc) ? current : acc;
		}, null),
	];

	return [
		resourceTimeLimits[0] ? resourceTimeLimits[0].toDate() : null,
		resourceTimeLimits[1] ? resourceTimeLimits[1].toDate() : null,
	];
}

export function submitForm(
	getState: () => IStore,
	dispatch: Dispatch<IStore>,
	t: any,
	workEditType: 'week' | 'day',
	ownProps: IEditWeekWorkTimeOwnProps | IEditDayWorkTimeOwnProps,
	form: IBookingTimeTableForm,
	weekCount?: number,
	startDate?: Date,
	endDate?: Date,
): Promise<any> {
	const timezone = ownProps.office?.zoneInfo;
	const monday = momentWithTimezone(timezone, ownProps.monday);
	const promises: Array<() => Promise<any>> = [];

	return getOfficeBookingTemplates(getState, ownProps.office.id, true).then((templates) => {
		const bookingTemplatesWithResource = templates.filter(
			(bookingTemplate) =>
				bookingTemplate.officeId === ownProps.office.id &&
				bookingTemplateResourceIds(bookingTemplate).includes(ownProps.resource.id),
		);

		if (!bookingTemplatesWithResource.length) {
			dispatch(requestFailure(t('Shop_WorkTime_EditHours_NoBookingTemplates')));
			return Promise.reject(t('Shop_WorkTime_EditHours_NoBookingTemplates'));
		}

		if (workEditType === 'week') {
			const allBookingTimeTables: IBookingTimeTable[] = [];
			const allPartialBookingTimeTables: IBookingTimeTable[] = [];

			const startDateWithTimezone = momentWithTimezone(timezone, startDate.toString()).toISOString();
			const endDateWithTimezone = momentWithTimezone(timezone, endDate.toString()).toISOString();

			promises.push(() =>
				dispatch(deleteAllTimeTablesBetweenTimes(startDateWithTimezone, endDateWithTimezone, ownProps.resource.id)),
			);

			if (form?.workTime && form.workTime.length) {
				let weeks = 0;
				let originalPassed = false;
				const arrayOfLastActiveDays: number[] = [];

				// Get last indexes of active days in each week
				for (let i = 0; i < form.workTime.length; i = i + 1) {
					const indexOfLastActiveDayInWeek = form.workTime[i].reduce((lastIndex, currentDay, index) => {
						if (currentDay.active) {
							return index;
						} else {
							return lastIndex;
						}
					}, -1);

					arrayOfLastActiveDays.push(indexOfLastActiveDayInWeek);
				}

				for (let i = 0; i < form.workTime.length + 1; i = i + 1) {
					// Reset loop if increment variable is higher than weekCount
					if (i === weekCount) {
						i = 0;
						weeks = weeks + weekCount;

						if (!originalPassed) {
							originalPassed = true;
						}
					}

					// If no index take last index of week
					const indexWithLastSetDay = arrayOfLastActiveDays[i] === -1 ? 6 : arrayOfLastActiveDays[i];

					// Break once the added workTime date exceeds the set endDate
					if (
						momentWithTimezone(timezone, form.workTime[i][indexWithLastSetDay].date)
							.add(weeks, 'weeks')
							.isAfter(endDateWithTimezone.toString()) ||
						i > form.workTime.length
					) {
						break;
					}

					for (let day = 0; day < 7; day = day + 1) {
						const value: any = form.workTime[i][day];
						// if original loop went through start adding weeks until endDate is exceeded
						const date = originalPassed
							? momentWithTimezone(timezone, form.workTime[i][day].date).add(weeks, 'weeks')
							: momentWithTimezone(timezone, form.workTime[i][day].date);
						const periods = value?.active ? value?.periods || [] : [];
						const periodGroups = normalizeBookingTimetablesSingleResource(periods, timezone);

						// create for remaining
						for (const periodGroup of periodGroups) {
							const startTime = combineDateAndTime(
								date,
								momentWithTimezone(timezone, periodGroup.startTime),
								true,
							).toISOString();
							const endTime = combineDateAndTime(
								date,
								momentWithTimezone(timezone, periodGroup.endTime),
								true,
							).toISOString();
							const bookingTimeTables = generateMultipleBookingTimeTables(
								ownProps.office.id,
								ownProps.resource.id,
								startTime,
								endTime,
								bookingTemplatesWithResource,
								timezone,
							);

							// Online & manual
							if (periodGroup.availabilityType === 0) {
								allBookingTimeTables.push(...bookingTimeTables);
							}
							// Manual only
							else if (periodGroup.availabilityType === 1) {
								const partiallyUnavailableTimeTable = generatePartiallyUnavailableBookingTimeTable(
									ownProps.office.id,
									ownProps.resource.id,
									startTime,
									endTime,
									timezone,
								);

								allBookingTimeTables.push(...bookingTimeTables);
								allPartialBookingTimeTables.push(partiallyUnavailableTimeTable);
							}
						}
					}
				}

				// FOR NEW TIMESLOT GENERATION
				if (allBookingTimeTables.length) {
					promises.push(() => dispatch(createMultipleBookingTimetables(true, allBookingTimeTables)));
				}
				if (allPartialBookingTimeTables.length) {
					promises.push(() =>
						dispatch(createMultiplePartiallyUnavailableBookingTimetables(true, allPartialBookingTimeTables)),
					);
				}
			}

			return sequential(promises)
				.then(() => {
					dispatch(setBookingTimetablesFiltersOffice(ownProps.office.id)).then(() => dispatch(closeModal()));
				})
				.catch((e) => {
					console.error(e);
				});
		} else if (workEditType === 'day') {
			const allBookingTimeTables: IBookingTimeTable[] = [];
			const allPartialBookingTimeTables: IBookingTimeTable[] = [];

			for (let day = 0; day < 7; day++) {
				if ('day' in ownProps && ownProps.day !== day) {
					continue;
				}

				const value: any = form.workTime[day];
				const date = monday.clone().add(day, 'days');
				const dateDayStart = momentWithTimezone(timezone, date).clone().startOf('day').toISOString();
				const dateDayEnd = momentWithTimezone(timezone, date).clone().endOf('day').toISOString();
				const periods = value?.active ? value?.periods || [] : [];
				const periodGroups = normalizeBookingTimetablesSingleResource(periods, timezone);

				promises.push(() => dispatch(deleteAllTimeTablesBetweenTimes(dateDayStart, dateDayEnd, ownProps.resource.id)));

				// create for remaining
				for (const periodGroup of periodGroups) {
					const startTime = combineDateAndTime(
						date,
						momentWithTimezone(timezone, periodGroup.startTime),
						true,
					).toISOString();
					const endTime = combineDateAndTime(
						date,
						momentWithTimezone(timezone, periodGroup.endTime),
						true,
					).toISOString();
					const bookingTimeTables = generateMultipleBookingTimeTables(
						ownProps.office.id,
						ownProps.resource.id,
						startTime,
						endTime,
						bookingTemplatesWithResource,
						timezone,
					);

					// Online & manual booking
					if (periodGroup.availabilityType === 0) {
						allBookingTimeTables.push(...bookingTimeTables);
					}
					// Manual booking
					else if (periodGroup.availabilityType === 1) {
						const partiallyUnavailableTimeTable = generatePartiallyUnavailableBookingTimeTable(
							ownProps.office.id,
							ownProps.resource.id,
							startTime,
							endTime,
							timezone,
						);

						allBookingTimeTables.push(...bookingTimeTables);
						allPartialBookingTimeTables.push(partiallyUnavailableTimeTable);
					}
				}
			}

			if (allBookingTimeTables.length) {
				promises.push(() => dispatch(createMultipleBookingTimetables(true, allBookingTimeTables)));
			}
			if (allPartialBookingTimeTables.length) {
				promises.push(() =>
					dispatch(createMultiplePartiallyUnavailableBookingTimetables(true, allPartialBookingTimeTables)),
				);
			}

			return sequential(promises)
				.then(() => {
					getBookingTimetables();
					dispatch(setBookingTimetablesFiltersOffice(ownProps.office.id)).then(() => dispatch(closeModal()));
				})
				.catch((e) => {
					console.error(e);
				});
		}
	});
}
